import React, { useState, useEffect } from 'react';

// SVG Icon Components
const Search = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const Calendar = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const CheckCircle = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22,4 12,14.01 9,11.01"></polyline>
  </svg>
);

const Leaf = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
  </svg>
);

const Scissors = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="6" cy="6" r="3"></circle>
    <path d="M8.12 8.12 12 12"></path>
    <path d="M20 4 8.12 15.88"></path>
    <circle cx="6" cy="18" r="3"></circle>
    <path d="M14.8 14.8 20 20"></path>
  </svg>
);

const Truck = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="1" y="3" width="15" height="13"></rect>
    <polygon points="16,8 20,8 23,11 23,16 16,16"></polygon>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="18.5" cy="18.5" r="2.5"></circle>
  </svg>
);

const ChevronRight = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

const MapPin = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

// Articles Data
const ARTICLES_DATA = [
  {
    id: 'fall-cleanup',
    title: 'Fall Yard Cleanup in The Villages: What to Do and What to Leave Alone',
    excerpt: 'A Florida fall cleanup isn\'t a Northern one. What to tidy now, what to save until after frost, and how fall and spring cleanups fit together.',
    readTime: '7 min read',
    category: 'Seasonal',
    url: '/articles/fall-cleanup-the-villages.html'
  },
  {
    id: 'push-mowing',
    title: 'Lightweight Mowing in The Villages — Why Equipment Weight Matters',
    excerpt: 'Most lawn companies show up with 700–900 lb commercial mowers. Here\'s why that weight slowly compacts sandy Villages soil, and what right-sized equipment does instead.',
    readTime: '9 min read',
    category: 'Lawn Care',
    url: '/articles/push-mowing-the-villages.html'
  },
  {
    id: 'when-to-aerate',
    title: 'When to Aerate Your Lawn in The Villages',
    excerpt: 'Timing is everything for aeration in Central Florida. Learn the optimal windows for St. Augustine, Bahia, and Zoysia in The Villages.',
    readTime: '8 min read',
    category: 'Lawn Care',
    url: '/articles/when-to-aerate-the-villages.html'
  },
  {
    id: 'lawn-treatment',
    title: 'Lawn Treatment in The Villages — What Does Your Grass Need?',
    excerpt: 'Most Villages lawns are over-fertilized with the wrong products. Here\'s what soil-first lawn treatment actually looks like.',
    readTime: '10 min read',
    category: 'Lawn Care',
    url: '/articles/lawn-treatment-the-villages.html'
  },
  {
    id: 'core-aeration-villages',
    title: 'Core Aeration Lawn Service The Villages: Test Your Soil First',
    excerpt: 'Planning lawn aeration? Learn why soil testing first saves you $300-600 yearly on unnecessary fertilizer. Professional aeration coordination with trusted specialists.',
    readTime: '13 min read',
    category: 'Lawn Care',
    url: '/articles/core-aeration-villages-guide.html'
  },
  {
    id: '5-signs-lawn-assessment',
    title: '5 Signs You Need a Professional Lawn Assessment',
    excerpt: 'Recognize these warning signs that indicate your Villages lawn needs expert evaluation and care.',
    readTime: '7 min read',
    category: 'Lawn Care',
    url: '/articles/5-signs-lawn-assessment.html',
    featured: true,
    fullContent: `Is your Villages lawn struggling despite your best efforts? Many homeowners in Central Florida face common lawn problems that require professional diagnosis. Here are five critical signs that indicate you need a comprehensive lawn and soil assessment from a qualified lawn care service.

**1. Persistent Brown or Yellow Patches**
If areas of your lawn remain brown or yellow despite regular watering and fertilization, this often indicates underlying soil pH imbalances, disease, or pest problems specific to The Villages' sandy soil conditions. A professional soil test can identify nutrient deficiencies and pH issues that home remedies can't fix.

**2. Uneven Growth Patterns**
When some sections of your lawn grow thick and healthy while others remain thin and sparse, you likely have inconsistent soil quality or drainage issues. Our comprehensive lawn assessment includes soil composition analysis to identify these problems.

**3. Persistent Weeds Despite Treatment**
If weeds keep returning despite herbicide applications, your lawn's health is compromised. Healthy grass naturally crowds out weeds, so persistent weed problems signal deeper issues with soil health, grass selection for Florida's climate, or improper lawn care practices.

**4. Water Pooling or Drainage Problems**
Standing water after rain or irrigation indicates soil compaction or drainage issues common in Villages properties. Our lawn aeration service combined with proper soil amendments can resolve these problems and prevent grass root damage.

**5. Declining Health Despite Regular Maintenance**
When your lawn continues to decline despite following standard lawn care recommendations, professional assessment can identify problems invisible to the untrained eye. Our $189 comprehensive evaluation includes soil pH testing, nutrient analysis, pest identification, and a customized care plan based on University of Florida Extension guidelines for Central Florida lawns.

Don't let lawn problems escalate. A professional assessment identifies root causes and provides targeted solutions, saving you time and money compared to trial-and-error treatments. Contact Deponch LLC today for expert lawn and soil assessment services in The Villages, FL.`
  },
  {
    id: 'soil-test-villages',
    title: 'Soil Testing in The Villages: What It Is and Why It Matters',
    excerpt: 'Complete guide to professional soil testing in The Villages. Learn why 75% of tested lawns have pH problems and how a $189 assessment can save you thousands.',
    readTime: '11 min read',
    category: 'Soil Health',
    url: '/articles/soil-test-villages-guide.html'
  },
  {
    id: 'fall-lawn-care',
    title: 'Fall Lawn Care Checklist for The Villages',
    excerpt: 'Essential fall lawn maintenance tasks to prepare your Central Florida lawn for the cooler months.',
    readTime: '9 min read',
    category: 'Seasonal Care',
    url: '/articles/fall-lawn-care-checklist.html'
  },
  {
    id: 'when-to-hire',
    title: 'When to Hire a Pro vs DIY Lawn Care',
    excerpt: 'Make smart decisions about which lawn care tasks to tackle yourself and when to call the professionals.',
    readTime: '9 min read',
    category: 'Advice',
    url: '/articles/when-to-hire-vs-diy.html'
  },
  {
    id: 'sandy-soil-compost',
    title: 'Improving Sandy Soil with Compost in The Villages',
    excerpt: 'Transform your Central Florida sandy soil into healthy, water-retentive lawn foundation with proper composting.',
    readTime: '10 min read',
    category: 'Soil Health',
    url: '/articles/sandy-soil-compost.html'
  },
  {
    id: 'hedge-trimming',
    title: 'Hedge Trimming Schedule for Villages Properties',
    excerpt: 'Keep your shrubs looking great year-round with proper timing and UF Extension guidelines.',
    readTime: '7 min read',
    category: 'Landscaping',
    url: '/articles/hedge-trimming.html'
  },
  {
    id: 'best-plants',
    title: 'Best Plants for Villages Climate',
    excerpt: 'Florida-Friendly plants that thrive in Central Florida with minimal maintenance.',
    readTime: '8 min read',
    category: 'Landscaping',
    url: '/articles/best-plants.html'
  }
];

// Services Data
const SERVICES_DATA = [
  {
    id: 'assessment',
    title: 'Professional Lawn & Soil Assessment',
    price: '$189',
    description: 'Complete lawn evaluation including soil testing, pH analysis, and problem diagnosis for Villages properties. Get a customized care plan based on your specific lawn conditions. Our comprehensive assessment identifies nutrient deficiencies, pest problems, disease issues, and provides science-based recommendations following University of Florida Extension guidelines for Central Florida lawns.',
    problem: 'Need to know what your Villages lawn actually needs',
    features: [
      'Comprehensive soil pH testing and analysis',
      'Lawn health and grass type identification',
      'Pest and weed identification',
      'Customized care plan for The Villages climate',
      'Written recommendations with timeline'
    ],
    icon: Search,
    popular: true
  },
  {
    id: 'grass-cutting',
    title: 'Premium Grass Cutting Service',
    price: 'Starting $85',
    description: 'Professional grass cutting with lightweight, right-sized equipment — not the heavy commercial mowers that compact your sandy Florida soil. A compact rider for open turf and a walk-behind for finish work protects your lawn\'s root zone, returns nutrient-rich clippings, and mows at the correct height for St. Augustine, Bahia, and Zoysia. Edge trimming and cleanup included every visit.',
    problem: 'Need dependable, soil-smart grass cutting in The Villages',
    features: [
      'Lightweight, right-sized equipment — no soil compaction',
      'Correct mowing height for Florida grass varieties',
      'Nutrient clipping return builds soil health over time',
      'Edge trimming and full cleanup every visit',
      'Weekly or bi-weekly scheduling',
      'HOA compliance guaranteed'
    ],
    icon: Scissors,
    popular: false
  },
  {
    id: 'lawn-treatment',
    title: 'Comprehensive Lawn Treatment Service',
    price: 'Custom Quote',
    description: 'Science-based lawn treatment including aeration, fertilization, weed control, and soil improvement for Villages properties. We follow University of Florida Extension recommendations for Central Florida lawns, ensuring your grass receives exactly what it needs when it needs it. Our treatments address the unique challenges of sandy Florida soil and help your lawn thrive year-round.',
    problem: 'Lawn needs professional treatment and care',
    features: [
      'Professional lawn aeration service',
      'Science-based fertilization program',
      'Targeted weed and pest control',
      'Soil pH correction and amendments',
      'Ongoing lawn health monitoring'
    ],
    icon: Truck,
    popular: false
  }
];

// Phone Reveal Component
const PhoneReveal = () => {
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [mouseMovements, setMouseMovements] = useState([]);

  const encodedPhone = 'KDM1MikgMzU2IC0gODg2OCA=';
  const realPhone = atob(encodedPhone);

  const calculateEntropy = (movements) => {
    if (movements.length < 10) return 0;
    const distances = [];
    for (let i = 1; i < movements.length; i++) {
      const dx = movements[i].x - movements[i - 1].x;
      const dy = movements[i].y - movements[i - 1].y;
      distances.push(Math.sqrt(dx * dx + dy * dy));
    }
    const avgDist = distances.reduce((a, b) => a + b, 0) / distances.length;
    return distances.reduce((sum, d) => sum + Math.pow(d - avgDist, 2), 0) / distances.length;
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (phoneRevealed) return;
      setMouseMovements(prev => {
        const updated = [...prev, { x: e.clientX, y: e.clientY, time: Date.now() }];
        if (updated.length > 50) updated.shift();
        if (updated.length >= 20 && calculateEntropy(updated) > 100) {
          setPhoneRevealed(true);
          if (window.gtag) {
            window.gtag('event', 'phone_reveal', {
              event_category: 'engagement',
              event_label: 'Human verification passed'
            });
          }
        }
        return updated;
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [phoneRevealed]);

  const handleTouch = () => {
    if (!phoneRevealed) setTimeout(() => setPhoneRevealed(true), 2000);
  };

  return (
    <div
      className={`mt-6 p-6 bg-white rounded-lg shadow-md inline-block ${!phoneRevealed ? 'cursor-pointer bg-green-50' : ''}`}
      onTouchStart={handleTouch}
    >
      {phoneRevealed ? (
        <a href={`tel:${realPhone.replace(/\D/g, '')}`} className="text-2xl font-bold text-green-600 no-underline">
          {realPhone}
        </a>
      ) : (
        <>
          <div className="text-2xl font-bold text-green-600">Move mouse to reveal phone</div>
          <div className="text-gray-600 text-sm mt-2">Bot protection active</div>
        </>
      )}
    </div>
  );
};

function App() {
  const [currentSection, setCurrentSection] = useState('home');
  const [articles] = useState(ARTICLES_DATA);
  const [services] = useState(SERVICES_DATA);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    serviceType: '',
    urgency: 'within-month',
    mainProblem: '',
    country: '',
    'bot-field': '',
    'form-start-time': Date.now()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Geo lookup on mount
  useEffect(() => {
    fetch('https://ipapi.co/country_code/')
      .then(res => res.text())
      .then(code => {
        setFormData(prev => ({ ...prev, country: code.trim() }));
      })
      .catch(() => {
        setFormData(prev => ({ ...prev, country: 'unknown' }));
      });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const timeOnPage = Date.now() - formData['form-start-time'];
    if (timeOnPage < 1500) {
      setSubmitted(true);
      return;
    }

    if (formData['bot-field']) {
      setSubmitted(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    if (formData.name.trim().length < 2) {
      setFormError('Please enter your name.');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('form-name', 'lawn-care-leads');
      Object.keys(formData).forEach(key => {
        if (key !== 'bot-field' && key !== 'form-start-time') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await fetch('/', {
        method: 'POST',
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(submitData).toString()
      });

      if (response.ok) {
        setSubmitted(true);
        if (window.gtag) {
          window.gtag('event', 'form_submission', {
            event_category: 'contact',
            event_label: 'Lawn care request',
            value: formData.serviceType
          });
        }
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const trackCTAClick = (label) => {
    if (window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'CTA',
        event_label: label
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden Netlify Form */}
      <form name="lawn-care-leads" data-netlify="true" data-netlify-honeypot="bot-field" hidden>
        <input type="hidden" name="form-name" value="lawn-care-leads" />
        <input name="bot-field" />
        <input name="name" />
        <input name="email" />
        <input name="phone" />
        <input name="address" />
        <input name="serviceType" />
        <input name="urgency" />
        <input name="mainProblem" />
        <input name="country" />
      </form>

      {/* SEASONAL CLEANUP BANNER */}
      <div className="bg-green-800 text-white text-center text-sm sm:text-base px-4 py-2">
        <span className="font-semibold">Fall &amp; spring cleanup packages available.</span>{' '}
        <a
          href="/articles/fall-cleanup-the-villages.html"
          onClick={() => trackCTAClick('Seasonal Banner - Cleanup Article')}
          className="underline text-green-100 hover:text-white"
        >
          See what&apos;s included
        </a>
      </div>

      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Leaf className="w-8 h-8 text-green-600" />
              <div>
                <div className="text-xl font-bold text-gray-900">Lawn & Soil Care Service</div>
                <p className="text-sm text-gray-600">Deponch LLC - Lawn Maintenance Company - The Villages, FL</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-6">
              <button
                onClick={() => setCurrentSection('home')}
                className={`text-sm font-medium ${currentSection === 'home' ? 'text-green-600' : 'text-gray-600 hover:text-green-600'}`}
              >
                Home
              </button>
              <button
                onClick={() => setCurrentSection('services')}
                className={`text-sm font-medium ${currentSection === 'services' ? 'text-green-600' : 'text-gray-600 hover:text-green-600'}`}
              >
                Services
              </button>
              <button
                onClick={() => setCurrentSection('articles')}
                className={`text-sm font-medium ${currentSection === 'articles' ? 'text-green-600' : 'text-gray-600 hover:text-green-600'}`}
              >
                Lawn Care Guides
              </button>
              <button
                onClick={() => {
                  setCurrentSection('request');
                  trackCTAClick('Header CTA');
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                Get Assessment
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {currentSection === 'home' && (
          <>
            {/* HERO */}
            <div className="text-center mb-16 pt-8">
              <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <MapPin className="w-4 h-4 mr-2" />
                Serving The Villages & Surrounding Areas
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Lawn Care The Villages, FL - Professional Service
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Expert lawn care services including soil assessment, grass cutting, lawn aeration, weed control, and pest control for Villages properties. Lightweight-equipment grass cutting and comprehensive soil testing.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button
                  onClick={() => {
                    window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
                    trackCTAClick('Hero CTA - Book & Pay');
                  }}
                  className="inline-flex items-center bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book & Pay $189 — Soil Assessment
                </button>
                <button
                  onClick={() => setCurrentSection('request')}
                  className="inline-flex items-center bg-white text-green-700 border-2 border-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-50 transition-colors"
                >
                  Have Questions? Contact Us
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              </div>

              <PhoneReveal />
            </div>

            {/* GRASS CUTTING SECTION */}
            <section className="bg-gray-900 text-white py-12 px-6 my-12 rounded-lg">
              <div className="max-w-4xl mx-auto">
                <div className="inline-flex items-center bg-green-700 text-green-100 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                  <Scissors className="w-4 h-4 mr-2" />
                  Premium Grass Cutting Service — The Villages, FL
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Heavy Mowers Are Quietly Compacting Your Lawn
                </h2>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  That 700–900 lb commercial mower your current grass cutting company shows up with? In Villages sandy soil, it's compacting your root zone every single visit. Compacted soil means poor drainage, shallow roots, and a lawn that needs more water, more fertilizer, and more treatments — not less.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-800 rounded-lg p-6">
                    <h3 className="font-bold text-red-400 mb-3 text-lg">❌ Heavy Commercial Mowers</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li>• 700–900 lbs compacting sandy soil every visit</li>
                      <li>• Compaction destroys root zone depth over time</li>
                      <li>• Creates ruts and uneven turf surface</li>
                      <li>• Promotes thatch buildup and poor drainage</li>
                      <li>• Scalping on uneven Village lot grades</li>
                      <li>• Built for multi-acre runs, not tight Villages lots</li>
                    </ul>
                  </div>
                  <div className="bg-green-900 rounded-lg p-6 border border-green-600">
                    <h3 className="font-bold text-green-300 mb-3 text-lg">✓ Lightweight, Right-Sized Equipment</h3>
                    <ul className="space-y-2 text-gray-200 text-sm">
                      <li>• Fraction of the weight — no soil compaction</li>
                      <li>• Precise height control for St. Augustine, Bahia & Zoysia</li>
                      <li>• Clean cut without scalping on grade changes</li>
                      <li>• Clipping return feeds soil — free slow-release nitrogen</li>
                      <li>• Better maneuverability around landscaping</li>
                      <li>• Quieter, cleaner, less disruptive to neighbors</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-green-800 border border-green-500 rounded-lg p-5 mb-6">
                  <p className="text-green-100 font-semibold mb-1">The Deponch Difference for Grass Cutting:</p>
                  <p className="text-gray-200 text-sm leading-relaxed">
                    We run a compact, lightweight rider for open turf and a walk-behind for edges, beds, and finish work — equipment sized for Villages lots, not commercial iron designed for multi-acre runs. When you pair smart mowing with our soil-first approach, your lawn builds health between visits instead of losing it.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => {
                      setCurrentSection('request');
                      trackCTAClick('Grass Cutting Section CTA');
                    }}
                    className="inline-flex items-center bg-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-400 transition-colors"
                  >
                    <Scissors className="w-5 h-5 mr-2" />
                    Get Grass Cutting Quote
                  </button>
                  <a
                    href="/articles/push-mowing-the-villages.html"
                    className="inline-flex items-center bg-transparent border border-gray-500 text-gray-300 px-8 py-3 rounded-lg font-semibold hover:border-green-400 hover:text-green-300 transition-colors no-underline"
                  >
                    Read: Why Equipment Weight Matters
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </a>
                </div>
              </div>
            </section>

            {/* CHEMICAL CAROUSEL SECTION */}
            <section className="bg-amber-50 border-l-4 border-amber-500 py-12 px-6 my-12 rounded-lg">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Are Monthly Lawn Treatments Making Your Soil Worse?
                </h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Most lawn care companies in The Villages follow the same generic monthly treatment schedules—applying
                  the same chemicals to every lawn, regardless of actual soil conditions. This "<strong>chemical carousel</strong>"
                  temporarily masks symptoms while making your underlying soil problems worse over time.
                </p>

                <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">The Chemical Carousel Cycle:</h3>
                  <ol className="space-y-3 text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-amber-600 flex-shrink-0">1.</span>
                      <span>Generic lawn fertilization temporarily greens up your grass</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-amber-600 flex-shrink-0">2.</span>
                      <span>Synthetic chemicals damage beneficial soil microbes in Villages' sandy soil</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-amber-600 flex-shrink-0">3.</span>
                      <span>Sandy soil loses water retention capacity without organic matter and proper mulching</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-amber-600 flex-shrink-0">4.</span>
                      <span>Your lawn needs MORE lawn treatment services, MORE often</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-amber-600 flex-shrink-0">5.</span>
                      <span>You're now dependent on monthly chemical applications forever—the opposite of sustainable lawn maintenance</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
                  <p className="text-green-900 font-medium mb-2">
                    ✓ The Deponch Difference: Soil-First Lawn Care The Villages
                  </p>
                  <p className="text-gray-700 mb-3">
                    We start with comprehensive soil testing and lawn health assessment to identify your lawn's actual deficiencies.
                    Our organic compost programs and professional lawn aeration service rebuild soil health, creating lawns that
                    require <em>fewer</em> treatments over time, not more.
                  </p>
                  <p className="text-gray-700">
                    Lightweight-equipment grass cutting, weed control, and mulching service included in our
                    comprehensive lawn maintenance programs.
                  </p>
                </div>

                <div className="text-center">
                  <button
                    onClick={() => {
                      window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
                      trackCTAClick('Chemical Carousel CTA');
                    }}
                    className="inline-flex items-center bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-md"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book $189 Soil Assessment Now
                  </button>
                </div>
              </div>
            </section>

            {/* VILLAGES SOIL SCIENCE SECTION */}
            <section className="bg-gradient-to-br from-green-50 to-blue-50 py-12 px-6 my-12 rounded-lg">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                  Why The Villages Soil Needs Specialized Lawn Care
                </h2>
                <p className="text-lg text-gray-700 text-center mb-8 max-w-3xl mx-auto">
                  The Villages crosses a variety of differing soil types, ridges, dunes and marine deposits,
                  which creates unique challenges for lawn care—especially for new construction lawns.
                  Generic "one-size-fits-all" grass cutting companies can't solve these problems with
                  basic lawn treatment service.
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl">⚠️</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900">Extreme Sandy Soil</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Villages soil is 95%+ sand with almost no organic matter. Water and nutrients drain away in hours,
                      not days. Professional soil testing and lawn health assessment reveals exactly what your lawn needs.
                      Regular mulching service helps retain moisture and build organic content over time.
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl">📊</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900">High Alkaline pH</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Alkaline soil (pH 7.5-8.5) locks up essential nutrients like iron and nitrogen, causing yellow,
                      weak grass despite lawn fertilization service. Our comprehensive lawn assessment identifies pH issues
                      that generic grass cutting companies miss. Proper weed control and pest control depend on balanced pH.
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl">💧</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900">Poor Water Retention</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Without organic matter, soil can't hold moisture. Your irrigation runs constantly, wasting water
                      and money on lawn maintenance. Professional lawn aeration service and organic compost programs
                      improve water retention by 50%+ in Villages' sandy conditions.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-green-500">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                    The Solution: Professional Soil Assessment & Lawn Care The Villages FL
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold text-green-700 mb-3">What Our Lawn Health Assessment Tests:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Soil pH levels and nutrient content for proper lawn fertilization</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Organic matter content and mulching needs</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Pest identification requiring pest control services</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Weed types requiring targeted weed control</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Soil compaction levels for lawn aeration service planning</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Water drainage capacity and irrigation efficiency</span></li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-green-700 mb-3">What Our Lawn Care Services Build:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Living soil with beneficial microbes through organic lawn treatment</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Improved water retention (50%+ less irrigation needed)</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Natural pH balance over time with proper lawn maintenance</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Stronger root systems through professional lawn aeration service</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Reduced fertilizer dependency with soil-first approach</span></li>
                        <li className="flex gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Healthier grass requiring less frequent grass cutting service</span></li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-gray-700 mb-4 text-center leading-relaxed">
                      Our soil-first lawn care approach for The Villages is backed by experience and
                      University of Florida Extension research for Central Florida conditions. We maintain
                      lawns with lightweight, right-sized mowers that return nutrient-rich clippings to build soil health,
                      combined with comprehensive assessment, targeted treatments when needed, and ongoing
                      maintenance plans designed to create sustainable lawns that improve over time.
                    </p>
                    <div className="text-center">
                      <button
                        onClick={() => {
                          window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
                          trackCTAClick('Soil Science Section CTA');
                        }}
                        className="inline-flex items-center bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-md"
                      >
                        <Search className="w-5 h-5 mr-2" />
                        Book Your $189 Assessment & Pay
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* SERVICE CARDS */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {services.map(service => (
                <div key={service.id} className={`bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow ${service.popular ? 'ring-2 ring-green-600' : ''}`}>
                  {service.popular && (
                    <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">
                      MOST POPULAR
                    </div>
                  )}
                  <service.icon className="w-12 h-12 text-green-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <div className="text-3xl font-bold text-green-600 mb-4">{service.price}</div>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      if (service.id === 'assessment') {
                        window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
                        trackCTAClick(`Service Card - ${service.id} - Payment`);
                      } else {
                        setCurrentSection('request');
                        trackCTAClick(`Service Card - ${service.id}`);
                      }
                    }}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    {service.id === 'assessment' ? 'Book & Pay $189' : 'Request Quote'}
                  </button>
                </div>
              ))}
            </div>

            {/* ARTICLES GRID */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Lawn Care Guides & Resources</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.filter(a => !a.featured).map(article => (
                  <div key={article.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-500">{article.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{article.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{article.excerpt}</p>
                    <a
                      href={article.url}
                      className="text-green-600 font-semibold text-sm hover:text-green-700 inline-flex items-center no-underline"
                    >
                      Read More
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {currentSection === 'services' && (
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Lawn Care Services</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {services.map(service => (
                <div key={service.id} className={`bg-white rounded-xl shadow-sm p-8 ${service.popular ? 'ring-2 ring-green-600' : ''}`}>
                  {service.popular && (
                    <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}
                  <service.icon className="w-16 h-16 text-green-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <div className="text-4xl font-bold text-green-600 mb-4">{service.price}</div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <p className="text-sm font-semibold text-green-800 mb-2">This service solves:</p>
                    <p className="text-sm text-green-700">{service.problem}</p>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      if (service.id === 'assessment') {
                        window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
                        trackCTAClick(`Service Detail - ${service.id} - Payment`);
                      } else {
                        setCurrentSection('request');
                        trackCTAClick(`Service Detail - ${service.id}`);
                      }
                    }}
                    className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    {service.id === 'assessment' ? 'Book & Pay $189 Now' : 'Request This Service'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentSection === 'articles' && (
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Lawn Care Guides</h2>
            <p className="text-xl text-gray-600 mb-8 text-center max-w-3xl mx-auto">
              Expert advice for maintaining healthy lawns in The Villages' unique climate
            </p>

            <div className="mb-8">
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {articles
                .filter(article =>
                  searchTerm === '' ||
                  article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(article => (
                  <div key={article.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-500">{article.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{article.title}</h3>
                    <p className="text-gray-600 mb-4">{article.excerpt}</p>
                    <a
                      href={article.url}
                      className="text-green-600 font-semibold hover:text-green-700 inline-flex items-center no-underline"
                    >
                      Read Full Article
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                ))}
            </div>
          </div>
        )}

        {currentSection === 'request' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Lawn Care Service</h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll contact you within 24 hours to schedule your service.
                </p>
              </div>

              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for contacting us. We'll reach out within 24 hours to discuss your lawn care needs.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setCurrentSection('home');
                    }}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Return to Home
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit}>
                  {formError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {formError}
                    </div>
                  )}

                  {/* Honeypot */}
                  <div className="hidden">
                    <input
                      type="text"
                      name="bot-field"
                      value={formData['bot-field']}
                      onChange={(e) => setFormData(prev => ({...prev, 'bot-field': e.target.value}))}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <input type="hidden" name="country" value={formData.country} />

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Villages Address (Optional)</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="For local service area verification"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">What lawn care service do you need? *</label>
                    <select
                      value={formData.serviceType}
                      onChange={(e) => setFormData(prev => ({...prev, serviceType: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">Choose your service...</option>
                      <option value="assessment">Lawn & Soil Assessment ($189) - What does my lawn need?</option>
                      <option value="grass-cutting">Grass Cutting Service - Lightweight equipment, weekly</option>
                      <option value="lawn-treatment">Lawn Treatment - Aeration, soil improvement, weed control</option>
                      <option value="multiple">Multiple Services - Assessment + ongoing lawn care</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Describe your lawn care needs</label>
                    <textarea
                      value={formData.mainProblem}
                      onChange={(e) => setFormData(prev => ({...prev, mainProblem: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows={3}
                      placeholder="Brown spots, need grass cutting, lawn aeration, soil problems, etc."
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">When do you need service?</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['This week', 'Within month', 'Not urgent', 'Just browsing'].map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData(prev => ({...prev, urgency: option.toLowerCase().replace(' ', '-')}))}
                          className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                            formData.urgency === option.toLowerCase().replace(' ', '-')
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-green-600'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending Request...' : 'Request Lawn Care Service'}
                  </button>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    By submitting this form, you agree to be contacted about lawn care services for your Villages property.
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-green-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold mb-6">Ready for Professional Lawn Care Service?</h3>
          <p className="text-xl mb-8 text-green-100">
            Expert lawn & soil assessment, premium grass cutting, aeration, and lawn care for your Villages property.
          </p>
          <button
            onClick={() => {
              window.location.href = 'https://link.waveapps.com/na7qq7-mvbkrn';
              trackCTAClick('Bottom CTA - Book & Pay');
            }}
            className="inline-flex items-center bg-white text-green-700 px-8 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book & Pay $189 - Soil Assessment
          </button>
        </div>
      </div>

      <footer className="bg-gray-900 text-gray-300 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <Leaf className="w-6 h-6 text-green-500" />
              <span className="font-semibold">Lawn & Soil Care Service The Villages FL</span>
            </div>
            <div className="text-sm text-gray-400">
              Serving The Villages & Central Florida
            </div>
          </div>
          <div className="text-center text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} Deponch LLC. The Villages, FL.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;